<template>
  <!-- 全屏模态框 -->
  <div v-if="show" class="fixed inset-0 z-50 bg-white flex flex-col">
    <!-- 模态框内容 - 真正全屏 -->
    <div class="flex-1 flex flex-col h-full overflow-hidden p-6">
      <!-- 标题 -->
      <div class="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 flex-shrink-0">
        <div>
          <h3 class="text-xl font-semibold text-gray-900">
            {{ isEditing ? '编辑客户' : '添加客户' }}
          </h3>
          <p class="text-sm text-gray-500 mt-1">
            {{ isEditing ? '修改客户的基本信息、标签分组和产品绑定' : '填写客户信息，创建新的客户档案' }}
          </p>
        </div>
        <button
          @click="handleClose"
          class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-all flex-shrink-0"
          title="关闭"
        >
          <XMarkIcon class="h-6 w-6" />
        </button>
      </div>

      <!-- 主体内容 - 可滚动区域 -->
      <div class="flex-1 overflow-y-auto min-h-0">
        <form @submit.prevent="handleSubmit" class="space-y-8">
          <!-- 基本信息 -->
          <div class="space-y-4">
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200">基本信息</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <!-- 邮箱 -->
              <div class="sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  邮箱地址 <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="formData.email"
                  type="email"
                  required
                  :readonly="!isEmailEditable"
                  :class="[
                    'w-full h-10 px-3 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                    isEmailEditable 
                      ? 'border-gray-300 bg-white' 
                      : 'border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed'
                  ]"
                  placeholder="客户的邮箱地址"
                />
                <!-- 子账号编辑时的提示 -->
                <p v-if="!isEmailEditable" class="mt-1 text-xs text-gray-500">
                  <svg class="inline-block h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  子账号不能修改已有客户的邮箱地址
                </p>
              </div>
              
              <!-- 名字 -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">名字</label>
                <input
                  v-model="formData.first_name"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="First Name"
                />
              </div>
              
              <!-- 姓氏 -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">姓氏</label>
                <input
                  v-model="formData.last_name"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="Last Name"
                />
              </div>
              
              <!-- 电话 -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">电话</label>
                <input
                  v-model="formData.phone"
                  type="tel"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="联系电话"
                />
              </div>
              
              <!-- 归属邮箱 -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">归属邮箱</label>
                <input
                  v-model="formData.owned_email_account"
                  type="email"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="客户归属的邮箱地址"
                />
              </div>
              
              <!-- 客户来源 -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">客户来源</label>
                <input
                  v-model="formData.source"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="如：网站注册、展会、推荐等"
                />
              </div>
              
              <!-- 订单号 -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">订单号</label>
                <input
                  v-model="formData.order_number"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="相关的订单编号"
                />
              </div>
              
              <!-- 备注 -->
              <div class="sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">备注</label>
                <textarea
                  v-model="formData.notes"
                  rows="4"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="备注信息"
                ></textarea>
              </div>
            </div>
          </div>

          <!-- 地址信息 -->
          <div class="space-y-4">
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200">地址信息</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">国家</label>
                <input
                  v-model="formData.country"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="国家"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">州/省</label>
                <input
                  v-model="formData.state"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="州/省"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">城市</label>
                <input
                  v-model="formData.city"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="城市"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">邮政编码</label>
                <input
                  v-model="formData.postal_code"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="邮政编码"
                />
              </div>
              
              <div class="sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">详细地址</label>
                <textarea
                  v-model="formData.address"
                  rows="3"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="详细地址"
                ></textarea>
              </div>
            </div>
          </div>

          <!-- 标签和分组 -->
          <div class="space-y-4">
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200">标签和分组</h4>
            <!-- 标签选择 -->
            <div>
              <div class="flex items-center justify-between mb-3">
                <label class="text-sm font-medium text-gray-700">标签</label>
                <input
                  v-model="tagSearchQuery"
                  type="text"
                  placeholder="搜索标签..."
                  class="w-32 h-7 px-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div class="max-h-40 overflow-y-auto pr-2">
                <div v-if="filteredTags.length > 0" class="flex flex-wrap gap-2">
                  <label v-for="tag in filteredTags" :key="tag.id" class="cursor-pointer">
                    <input 
                      type="checkbox" 
                      :value="tag.id"
                      v-model="formData.tag_ids"
                      class="sr-only" 
                    />
                    <span 
                      class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
                      :class="formData.tag_ids.includes(tag.id) 
                        ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                        : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200'"
                      :style="formData.tag_ids.includes(tag.id) 
                        ? { backgroundColor: tag.background_color || '#DBEAFE', color: tag.text_color || '#1E40AF' } 
                        : {}"
                    >
                      {{ tag.name }}
                    </span>
                  </label>
                </div>
                <div v-else class="text-center py-4 text-sm text-gray-400">
                  {{ tagSearchQuery ? '未找到匹配的标签' : '暂无可用标签' }}
                </div>
              </div>
            </div>
            
            <!-- 分组选择 -->
            <div>
              <div class="flex items-center justify-between mb-3">
                <label class="text-sm font-medium text-gray-700">
                  分组 <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="groupSearchQuery"
                  type="text"
                  placeholder="搜索分组..."
                  class="w-32 h-7 px-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div class="max-h-40 overflow-y-auto pr-2">
                <div v-if="filteredGroups.length > 0" class="flex flex-wrap gap-2">
                  <label v-for="group in filteredGroups" :key="group.id" class="cursor-pointer">
                    <input 
                      type="checkbox" 
                      :value="group.id"
                      v-model="formData.group_ids"
                      class="sr-only" 
                    />
                    <span 
                      class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
                      :class="formData.group_ids.includes(group.id) 
                        ? 'bg-green-100 text-green-800 border border-green-300' 
                        : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200'"
                    >
                      {{ group.name }}
                      <span v-if="group.description" class="ml-1 text-gray-500">· {{ group.description }}</span>
                    </span>
                  </label>
                </div>
                <div v-else class="text-center py-4 text-sm text-gray-400">
                  {{ groupSearchQuery ? '未找到匹配的分组' : '暂无可用分组' }}
                </div>
              </div>
              <p v-if="formData.group_ids.length === 0" class="text-xs text-red-500 mt-2">请至少选择一个分组</p>
            </div>
          </div>

          <!-- 产品绑定 -->
          <div class="space-y-4">
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200">产品绑定</h4>
            <!-- 产品搜索 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-3">搜索产品</label>
              <div class="relative">
                <input
                  v-model="productSearchQuery"
                  @input="searchProducts"
                  type="text"
                  class="w-full h-10 pl-10 pr-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="输入产品名称或编号进行搜索..."
                />
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <div v-if="isSearchingProducts" class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg class="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                
                <!-- 搜索结果 -->
                <div v-if="productSearchResults.length > 0" class="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  <div 
                    v-for="product in productSearchResults" 
                    :key="product.id" 
                    @click="addProduct(product)"
                    class="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  >
                    <div class="flex-1">
                      <div class="font-medium text-gray-900">{{ product.name }}</div>
                      <div class="text-sm text-gray-500 font-mono">{{ product.code }}</div>
                      <div v-if="product.brand" class="text-xs text-gray-400">{{ product.brand }}</div>
                    </div>
                    <button class="ml-3 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                      添加
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 已绑定产品列表 -->
            <div>
              <div class="flex items-center justify-between mb-3">
                <label class="block text-sm font-medium text-gray-700">
                  已绑定产品
                </label>
                <span class="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                  {{ boundProducts.length }}
                </span>
              </div>
              
              <div v-if="boundProducts.length === 0" class="text-center text-gray-500 py-8 border-2 border-dashed border-gray-200 rounded-lg">
                <svg class="mx-auto h-12 w-12 text-gray-400 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M20 7l-8-4-8 4m16 0l-8 4.5m8-4.5v10l-8 4.5m0-9L4 7m8 4.5l8-4.5m-8 4.5L4 17" />
                </svg>
                <p class="text-sm">暂无绑定产品</p>
                <p class="text-xs text-gray-400">请在上方搜索并添加产品</p>
              </div>
              
              <div v-else class="space-y-2 max-h-48 overflow-y-auto">
                <div 
                  v-for="product in boundProducts" 
                  :key="product.id"
                  class="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                >
                  <div class="flex-1">
                    <div class="font-medium text-gray-900 text-sm">{{ product.name }}</div>
                    <div class="text-xs text-gray-500 font-mono">{{ product.code }}</div>
                    <div v-if="product.brand" class="text-xs text-gray-400">{{ product.brand }}</div>
                  </div>
                  <button 
                    @click="removeProduct(product.id)"
                    class="ml-3 p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                    title="移除产品"
                  >
                    <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 自定义字段 -->
          <div v-if="hasCustomFields" class="space-y-4">
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200">自定义字段</h4>
            <div v-if="customFields.length === 0" class="text-center py-8 text-gray-500">
              暂无自定义字段
            </div>
            <div v-else class="space-y-4">
              <div v-for="field in customFields" :key="field.id">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  {{ field.label }}
                  <span v-if="field.is_required" class="text-red-500">*</span>
                </label>

                <!-- 文本输入 -->
                <input
                  v-if="field.field_type === 'text'"
                  v-model="customFieldValues[field.name]"
                  type="text"
                  :placeholder="field.placeholder"
                  :maxlength="field.max_length"
                  :disabled="isSubmitting"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >

                <!-- 多行文本 -->
                <textarea
                  v-else-if="field.field_type === 'textarea'"
                  v-model="customFieldValues[field.name]"
                  rows="3"
                  :placeholder="field.placeholder"
                  :maxlength="field.max_length"
                  :disabled="isSubmitting"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
                ></textarea>

                <!-- 数字输入 -->
                <input
                  v-else-if="field.field_type === 'number'"
                  v-model.number="customFieldValues[field.name]"
                  type="number"
                  :placeholder="field.placeholder"
                  :min="field.min_value"
                  :max="field.max_value"
                  :disabled="isSubmitting"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >

                <!-- 小数输入 -->
                <input
                  v-else-if="field.field_type === 'decimal'"
                  v-model.number="customFieldValues[field.name]"
                  type="number"
                  step="0.01"
                  :placeholder="field.placeholder"
                  :min="field.min_value"
                  :max="field.max_value"
                  :disabled="isSubmitting"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >

                <!-- 日期输入 -->
                <input
                  v-else-if="field.field_type === 'date'"
                  v-model="customFieldValues[field.name]"
                  type="date"
                  :disabled="isSubmitting"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >

                <!-- 布尔值选择 -->
                <div v-else-if="field.field_type === 'boolean'" class="flex items-center">
                  <input
                    v-model="customFieldValues[field.name]"
                    :id="`field_${field.name}`"
                    type="checkbox"
                    :disabled="isSubmitting"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  >
                  <label :for="`field_${field.name}`" class="ml-2 block text-sm text-gray-700">
                    {{ field.help_text || '启用' }}
                  </label>
                </div>

                <!-- 单选 -->
                <select
                  v-else-if="field.field_type === 'choice'"
                  v-model="customFieldValues[field.name]"
                  :disabled="isSubmitting"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="">请选择...</option>
                  <option
                    v-for="option in field.choice_options"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>

                <!-- 网址输入 -->
                <input
                  v-else-if="field.field_type === 'url'"
                  v-model="customFieldValues[field.name]"
                  type="url"
                  :placeholder="field.placeholder"
                  :disabled="isSubmitting"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >

                <!-- 邮箱输入 -->
                <input
                  v-else-if="field.field_type === 'email'"
                  v-model="customFieldValues[field.name]"
                  type="email"
                  :placeholder="field.placeholder"
                  :disabled="isSubmitting"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >

                <!-- 帮助文本 -->
                <p v-if="field.help_text && field.field_type !== 'boolean'" class="mt-1 text-sm text-gray-500">
                  {{ field.help_text }}
                </p>
              </div>
            </div>
          </div>

        </form>
      </div>

      <!-- 底部按钮 - 固定在底部 -->
      <div class="border-t border-gray-200 pt-4 mt-4 bg-white flex-shrink-0">
        <div class="flex items-center justify-end space-x-3">
          <button
            @click="handleClose"
            type="button"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
          >
            取消
          </button>
          <button
            @click="handleSubmit"
            :disabled="isSubmitting || formData.group_ids.length === 0"
            type="button"
            class="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ isSubmitting ? '保存中...' : (isEditing ? '保存修改' : '创建客户') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed, onMounted } from 'vue'
import { customerService, customerCustomFieldService } from '@/services/customerService'
import { useToast } from 'vue-toastification'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores/auth'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  customer: {
    type: Object,
    default: null
  },
  availableTags: {
    type: Array,
    default: () => []
  },
  availableGroups: {
    type: Array,
    default: () => []
  },
  customFieldsUpdateTrigger: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['close', 'success'])

const toast = useToast()
const authStore = useAuthStore()
const isSubmitting = ref(false)

// 自定义字段相关状态
const customFields = ref([])
const customFieldValues = ref({})

// 子账号权限检查
const isSubAccount = computed(() => {
  return authStore.user?.account_type === 'sub'
})

// 邮箱是否可编辑（子账号编辑客户时不可编辑，但添加客户时可以编辑）
const isEmailEditable = computed(() => {
  // 主账号总是可以编辑
  if (!isSubAccount.value) {
    return true
  }
  
  // 子账号：添加客户时可以编辑，编辑客户时不可以编辑
  return !isEditing.value
})

const isEditing = computed(() => !!props.customer)

// 计算属性：是否有自定义字段
const hasCustomFields = computed(() => {
  return customFields.value.length > 0
})

// 过滤后的标签
const filteredTags = computed(() => {
  if (!tagSearchQuery.value) {
    return props.availableTags || []
  }
  const query = tagSearchQuery.value.toLowerCase()
  return (props.availableTags || []).filter(tag => 
    tag.name.toLowerCase().includes(query)
  )
})

// 过滤后的分组
const filteredGroups = computed(() => {
  if (!groupSearchQuery.value) {
    return props.availableGroups || []
  }
  const query = groupSearchQuery.value.toLowerCase()
  return (props.availableGroups || []).filter(group => 
    group.name.toLowerCase().includes(query) ||
    (group.description && group.description.toLowerCase().includes(query))
  )
})

const formData = reactive({
  email: '',
  owned_email_account: '',  // 归属邮箱（邮箱地址字符串）
  first_name: '',
  last_name: '',
  phone: '',
  country: '',
  state: '',
  city: '',
  address: '',
  postal_code: '',
  source: '',
  order_number: '',
  notes: '',
  tag_ids: [],
  group_ids: [],
  product_ids: []
})

// 标签和分组搜索状态
const tagSearchQuery = ref('')
const groupSearchQuery = ref('')

// 产品绑定相关状态
const boundProducts = ref([])
const productSearchQuery = ref('')
const productSearchResults = ref([])
const isSearchingProducts = ref(false)

// 重置自定义字段值为默认值
const resetCustomFieldValues = () => {
  customFields.value.forEach(field => {
    if (field.field_type === 'boolean') {
      customFieldValues.value[field.name] = field.default_value === 'true' || field.default_value === true
    } else {
      customFieldValues.value[field.name] = field.default_value || ''
    }
  })
}

// 加载自定义字段
const loadCustomFields = async () => {
  try {
    const response = await customerCustomFieldService.getCustomFields()
    if (response.success) {
      customFields.value = response.data.results || []
      
      // 初始化自定义字段值对象
      resetCustomFieldValues()
    }
  } catch (error) {
    console.error('加载自定义字段失败:', error)
  }
}

// 监听props变化，初始化表单数据
watch(() => [props.show, props.customer], async ([newShow, newCustomer]) => {
  if (newShow) {
    // 确保自定义字段已加载（只在首次加载时请求）
    if (customFields.value.length === 0) {
      await loadCustomFields()
    }
    
    // 先重置所有自定义字段值为默认值
    resetCustomFieldValues()
    
    if (newCustomer) {
      // 编辑模式：填充现有数据
      Object.assign(formData, {
        email: newCustomer.email || '',
        owned_email_account: newCustomer.owned_email_account || '',
        first_name: newCustomer.first_name || '',
        last_name: newCustomer.last_name || '',
        phone: newCustomer.phone || '',
        country: newCustomer.country || '',
        state: newCustomer.state || '',
        city: newCustomer.city || '',
        address: newCustomer.address || '',
        postal_code: newCustomer.postal_code || '',
        source: newCustomer.source || '',
        order_number: newCustomer.order_number || '',
        notes: newCustomer.notes || '',
        tag_ids: newCustomer.tags?.map(tag => tag.id) || [],
        group_ids: newCustomer.groups?.map(group => group.id) || [],
        product_ids: newCustomer.products?.map(product => product.id) || []
      })
      // 设置绑定产品列表
      boundProducts.value = newCustomer.products || []
      
      // 填充自定义字段值
      if (newCustomer.custom_fields) {
        newCustomer.custom_fields.forEach(fieldData => {
          const field = customFields.value.find(f => f.id === fieldData.field_id)
          if (field) {
            if (field.field_type === 'boolean') {
              customFieldValues.value[field.name] = fieldData.raw_value === true
            } else {
              customFieldValues.value[field.name] = fieldData.raw_value || ''
            }
          }
        })
      }
    } else {
      // 添加模式：重置表单
      Object.assign(formData, {
        email: '',
        owned_email_account: '',
        first_name: '',
        last_name: '',
        phone: '',
        country: '',
        state: '',
        city: '',
        address: '',
        postal_code: '',
        source: '',
        order_number: '',
        notes: '',
        tag_ids: [],
        group_ids: [],
        product_ids: []
      })
      // 清空绑定产品列表
      boundProducts.value = []
    }
    
    // 重置产品搜索状态
    productSearchQuery.value = ''
    productSearchResults.value = []
    isSearchingProducts.value = false
    
    // 重置标签和分组搜索状态
    tagSearchQuery.value = ''
    groupSearchQuery.value = ''
  } else {
    // 🔧 模态框关闭时，清空所有表单数据
    Object.assign(formData, {
      email: '',
      owned_email_account: '',
      first_name: '',
      last_name: '',
      phone: '',
      country: '',
      state: '',
      city: '',
      address: '',
      postal_code: '',
      source: '',
      order_number: '',
      notes: '',
      tag_ids: [],
      group_ids: [],
      product_ids: []
    })
    // 清空绑定产品列表
    boundProducts.value = []
    // 重置自定义字段值为默认值
    resetCustomFieldValues()
    // 重置产品搜索状态
    productSearchQuery.value = ''
    productSearchResults.value = []
    isSearchingProducts.value = false
    // 重置标签和分组搜索状态
    tagSearchQuery.value = ''
    groupSearchQuery.value = ''
  }
}, { immediate: true })

// 监听自定义字段更新触发器
watch(() => props.customFieldsUpdateTrigger, () => {
  if (props.customFieldsUpdateTrigger > 0) {
    console.log('检测到自定义字段更新，重新加载自定义字段数据...')
    loadCustomFields()
  }
})

// 组件挂载时确保自定义字段已加载
onMounted(() => {
  if (customFields.value.length === 0) {
    loadCustomFields()
  }
})

const handleClose = () => {
  if (!isSubmitting.value) {
    emit('close')
  }
}

// 产品搜索方法
const searchProducts = async () => {
  const query = productSearchQuery.value.trim()
  if (query.length < 2) {
    productSearchResults.value = []
    return
  }
  
  isSearchingProducts.value = true
  try {
    // 直接导入productService
    const productServiceModule = await import('@/services/productService')
    const productService = productServiceModule.default || productServiceModule.productService
    
    if (!productService || typeof productService.getProducts !== 'function') {
      console.error('productService未正确导入:', productService)
      toast.error('产品服务不可用')
      return
    }
    
    const result = await productService.getProducts({ search: query, page_size: 10 })
    
    if (result && result.success) {
      // 过滤掉已经绑定的产品
      const boundProductIds = boundProducts.value.map(p => p.id)
      productSearchResults.value = (result.results || []).filter(product => 
        !boundProductIds.includes(product.id)
      )
    } else {
      productSearchResults.value = []
      if (result && result.message) {
        toast.error(result.message)
      }
    }
  } catch (error) {
    console.error('搜索产品失败:', error)
    toast.error('搜索产品失败')
    productSearchResults.value = []
  } finally {
    isSearchingProducts.value = false
  }
}

// 添加产品到绑定列表
const addProduct = (product) => {
  if (boundProducts.value.find(p => p.id === product.id)) {
    return
  }
  
  boundProducts.value.push(product)
  formData.product_ids.push(product.id)
  
  // 从搜索结果中移除
  productSearchResults.value = productSearchResults.value.filter(p => p.id !== product.id)
  toast.success(`已添加产品 ${product.name}`)
}

// 从绑定列表移除产品
const removeProduct = (productId) => {
  boundProducts.value = boundProducts.value.filter(p => p.id !== productId)
  formData.product_ids = formData.product_ids.filter(id => id !== productId)
  
  // 如果有搜索词，重新搜索以更新结果
  if (productSearchQuery.value.trim()) {
    searchProducts()
  }
}

const handleSubmit = async () => {
  // 基本验证
  if (!formData.email) {
    toast.error('请输入邮箱地址')
    return
  }
  
  if (formData.group_ids.length === 0) {
    toast.error('请至少选择一个分组')
    return
  }
  
  // 验证必填的自定义字段
  const missingRequiredFields = []
  customFields.value.forEach(field => {
    if (field.is_required) {
      const value = customFieldValues.value[field.name]
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        missingRequiredFields.push(field.label)
      }
    }
  })
  
  if (missingRequiredFields.length > 0) {
    toast.error(`请填写必填的自定义字段：${missingRequiredFields.join('、')}`)
    return
  }
  
  isSubmitting.value = true
  
  try {
    let result
    const submitData = { ...formData }
    // 确保product_ids是当前绑定产品的ID数组
    submitData.product_ids = boundProducts.value.map(p => p.id)
    
    // 添加自定义字段值
    const customFieldData = []
    customFields.value.forEach(field => {
      const value = customFieldValues.value[field.name]
      
      // 对于编辑模式，包含所有字段（包括空值），以便后端知道哪些字段需要清空
      // 对于新建模式，只包含有值的字段
      if (isEditing.value || (value !== undefined && value !== null && value !== '')) {
        let finalValue = value
        
        // 处理不同字段类型的值
        if (field.field_type === 'boolean') {
          finalValue = value ? 'true' : 'false'
        } else if (value === undefined || value === null) {
          finalValue = '' // 将 undefined/null 转换为空字符串
        } else {
          finalValue = String(value)
        }
        
        customFieldData.push({
          field_id: field.id,
          value: finalValue
        })
      }
    })
    
    // 如果有自定义字段定义，或者是编辑模式，都发送 custom_field_values
    if (customFieldData.length > 0 || (isEditing.value && customFields.value.length > 0)) {
      submitData.custom_field_values = customFieldData
    }
    
    if (isEditing.value) {
      // 编辑客户
      result = await customerService.updateCustomer(props.customer.id, submitData)
    } else {
      // 创建客户
      result = await customerService.createCustomer(submitData)
    }
    
    if (result.success) {
      toast.success(isEditing.value ? '客户信息已更新' : '客户创建成功')
      emit('success', result.data)
      emit('close')
    } else {
      // 🔧 解析并显示详细的字段错误
      if (result.errors) {
        const errorMessages = new Set() // 🔧 使用 Set 自动去重
        
        // 字段名映射（更友好的显示）
        const fieldNameMap = {
          'email': '邮箱地址',
          'first_name': '名字',
          'last_name': '姓氏',
          'phone': '电话',
          'country': '国家',
          'state': '州/省',
          'city': '城市',
          'address': '详细地址',
          'postal_code': '邮政编码',
          'source': '客户来源',
          'order_number': '订单号',
          'owned_email_account': '归属邮箱',
          'tag_ids': '标签',
          'group_ids': '分组',
          'product_ids': '产品'
        }
        
        // 遍历所有字段错误
        Object.keys(result.errors).forEach(fieldName => {
          const fieldErrors = result.errors[fieldName]
          
          // 处理数组形式的错误（DRF 验证错误格式）
          if (Array.isArray(fieldErrors)) {
            fieldErrors.forEach(error => {
              // 错误可能是字符串或对象 { string: "...", code: "..." }
              const errorMsg = typeof error === 'string' ? error : (error.string || error.message || error)
              
              const friendlyFieldName = fieldNameMap[fieldName] || fieldName
              const fullErrorMessage = `${friendlyFieldName}：${errorMsg}`
              
              // 🔧 Set 会自动去重相同的错误消息
              errorMessages.add(fullErrorMessage)
            })
          }
        })
        
        // 显示所有错误信息（已去重）
        if (errorMessages.size > 0) {
          errorMessages.forEach(msg => toast.error(msg))
        } else {
          // 如果没有解析到具体错误，显示通用消息
          toast.error(result.message || (isEditing.value ? '更新客户失败' : '创建客户失败'))
        }
      } else {
        // 没有详细错误信息，显示通用消息
        toast.error(result.message || (isEditing.value ? '更新客户失败' : '创建客户失败'))
      }
    }
  } catch (error) {
    console.error('提交客户信息失败:', error)
    toast.error('操作失败，请稍后重试')
  } finally {
    isSubmitting.value = false
  }
}
</script>
